import * as t from 'io-ts';
import _, {flatMap, isArray, isObject} from 'lodash';

import {hasProperty} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {TwoDimensionalMap} from '../../helpers/dataStructures/twoDimensionalMap';
import {RefType} from '../../models/api/apiTypes';
import {AppState, RAppState} from '../../store/appState';
import {ResourceMapAffinitiesEnum, ResourceMapType} from '../../store/appTypes';
import {RemovalCandidatesTree} from './gcDataStructures';
import {doesTypeContainResourceMapMemoized, extractValueAtPath} from './gcHelpers';
import {GarbageCollectorSearchingState, GarbageCollectorStagesEnum, ResourceMapDefinition} from './gcTypes';

/*
 * Phase #1: Initialize the garbage collector by finding removal candidates.
 */

export function performInitialScan(state: AppState): GarbageCollectorSearchingState {
  const mapDefinitions = findResourceMaps([], state, RAppState, state);

  const removalCandidates = new RemovalCandidatesTree();
  _(mapDefinitions)
    // Only keep transient maps.
    .filter(({initialAffinity}) => initialAffinity === ResourceMapAffinitiesEnum.TRANSIENT)
    .forEach(({path}) => {
      const map = extractValueAtPath(path, state);
      if (!map) {
        return [];
      }

      return _(map)
        .keys()
        .forEach((key) => removalCandidates.add([...path, key]));
    });

  const reachableItem = {
    obj: state,
    type: RAppState,
    path: [],
    isTransient: false,
  };

  return {
    removalCandidates,
    mapDefinitions,
    unscannedItems: [reachableItem],
    scannedItems: new TwoDimensionalMap(),
    lastScannedState: state,
    stage: GarbageCollectorStagesEnum.SEARCHING,
  };
}

/*
 * Helpers.
 */

const defaultGetShouldPersist = () => false;

function findResourceMaps(
  currentPath: ReadonlyArray<string>,
  obj: unknown,
  type: t.Type<any>,
  state: AppState,
): ReadonlyArray<ResourceMapDefinition> {
  // If we know this type doesn't contain any resource maps, we can stop searching early.
  if (!doesTypeContainResourceMapMemoized(type)) {
    return [];
  }

  // Resource map.
  if (type instanceof ResourceMapType) {
    return [
      {
        path: currentPath,
        type,
        initialAffinity: type.getAffinity(state),
        getShouldPersist: type.getShouldPersist || defaultGetShouldPersist,
      },
    ];
  }

  // RefType.
  if (type instanceof RefType) {
    return [];
  }

  // Undefined/null type.
  if (type instanceof t.UndefinedType || type instanceof t.NullType || type instanceof t.UnknownType) {
    return [];
  }

  // Primitives.
  if (
    type instanceof t.StringType ||
    type instanceof t.NumberType ||
    type instanceof t.LiteralType ||
    type instanceof t.BooleanType
  ) {
    return [];
  }

  // Wrapper types.
  if (type instanceof t.RefinementType || type instanceof t.ReadonlyType) {
    return findResourceMaps(currentPath, obj, type.type, state);
  }

  // Array types.
  if ((type instanceof t.ArrayType || type instanceof t.ReadonlyArrayType) && isArray(obj)) {
    // We can't have resource maps inside arrays since the paths to the resources
    // will change if the array is reordered.
    const resourceMapsInArray = flatMap(obj, (value, index) =>
      findResourceMaps([...currentPath, `${index}`], value, type.type, state),
    );
    if (resourceMapsInArray.length > 0) {
      throw new Error('Found resource map in array.');
    }

    return [];
  }

  // Dictionary type.
  if (type instanceof t.DictionaryType && isObject(obj)) {
    return flatMap(Object.entries(obj), ([key, value]) =>
      findResourceMaps([...currentPath, key], value, type.codomain, state),
    );
  }

  // Tagged union type.
  if (type instanceof t.TaggedUnionType) {
    return flatMap(type.types, (subType: t.Type<any>) => findResourceMaps(currentPath, obj, subType, state));
  }

  // Union / Intersection types.
  if (type instanceof t.UnionType || type instanceof t.IntersectionType) {
    return flatMap(type.types, (subType: t.Type<any>) => findResourceMaps(currentPath, obj, subType, state));
  }

  // Interface type.
  if (type instanceof t.InterfaceType || type instanceof t.PartialType) {
    return flatMap(type.props, (propType: t.Type<any>, key) => {
      if (!hasProperty(obj, key)) {
        return [];
      }

      return findResourceMaps([...currentPath, key], obj[key], propType, state);
    });
  }

  return [];
}
